import { useState, useMemo, useEffect } from 'react'
import {
  X,
  Bot,
  Brain,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Zap,
  FileText,
  Terminal,
  Cpu,
  Shield,
  Lightbulb,
  Leaf,
  Compass,
  PenTool,
  Loader2,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  MessageSquarePlus,
  Send,
  Eye,
} from 'lucide-react'
import type { AgentEvent } from '../types'
import {
  effectiveEventKind,
  effectiveMessageId,
  effectivePipelineStep,
  effectiveRunId,
  effectiveTextDelta,
  aggregatedTextMessageBodies,
} from '../agui/agUiNormalization'
import { feasibilityLabel } from '../lib/labels'

/** Referência do ADR em revisão — repassada nas ações HITL. */
export interface HitlAdrRef {
  adrId?: string | null
  adrTitle?: string | null
}

interface AgentReasoningPanelProps {
  events: AgentEvent[]
  isOpen: boolean
  onClose: () => void
  onHitlApprove?: (adr?: HitlAdrRef) => void
  onHitlReject?: (adr?: HitlAdrRef) => void
  onHitlOverride?: (feedback: string, adr?: HitlAdrRef) => void
}

const STEPS = [
  { key: 'archie.ingest', label: 'Ingestão de contexto', agent: 'archie', icon: Zap, short: 'Ingestão' },
  { key: 'dochie.review', label: 'Análise multidimensional', agent: 'dochie', icon: Brain, short: 'Análise' },
  { key: 'archie.synthesis', label: 'Síntese arquitetural (SDD + ADRs)', agent: 'archie', icon: FileText, short: 'Síntese' },
  { key: 'jobs.event_storm', label: 'Event Storming', agent: 'jobs', icon: Compass, short: 'E. Storm' },
  { key: 'vangogh.diagrams', label: 'Diagramas Mermaid', agent: 'vangogh', icon: PenTool, short: 'Diagramas' },
  { key: 'vangogh.prototype', label: 'Protótipo visual', agent: 'vangogh', icon: PenTool, short: 'Protótipo' },
] as const

const AGENT_META: Record<string, { name: string; color: string; bg: string; border: string; badge: string }> = {
  archie: {
    name: 'Archie',
    color: 'text-violet-600 dark:text-violet-400',
    bg: 'bg-violet-50 dark:bg-violet-900/20',
    border: 'border-violet-200 dark:border-violet-800',
    badge: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300',
  },
  dochie: {
    name: 'Dochie',
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800',
    badge: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
  },
  jobs: {
    name: 'Jobs',
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  },
  vangogh: {
    name: 'Van Gogh',
    color: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-50 dark:bg-rose-900/20',
    border: 'border-rose-200 dark:border-rose-800',
    badge: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300',
  },
}

const REVIEWER_META: Record<string, { name: string; icon: typeof Bot; color: string; bg: string }> = {
  tech_reviewer: { name: 'Técnico', icon: Cpu, color: 'text-blue-600', bg: 'bg-blue-50' },
  risk_reviewer: { name: 'Riscos', icon: Shield, color: 'text-red-600', bg: 'bg-red-50' },
  innovation_reviewer: { name: 'Inovação', icon: Lightbulb, color: 'text-yellow-600', bg: 'bg-yellow-50' },
  sustainability_reviewer: { name: 'Sustentabilidade', icon: Leaf, color: 'text-green-600', bg: 'bg-green-50' },
}

/** Map message_id suffix to step key (alinhado aos novos stepNames do team.py) */
function stepFromMessageId(msgId: string): string | null {
  if (msgId.endsWith('-ingest')) return 'archie.ingest'
  if (
    msgId.includes('tech_reviewer') ||
    msgId.includes('risk_reviewer') ||
    msgId.includes('innovation_reviewer') ||
    msgId.includes('sustainability_reviewer')
  ) {
    return 'dochie.review'
  }
  if (msgId.endsWith('-architect')) return 'archie.synthesis'
  if (msgId.endsWith('-jobs-event-storm')) return 'jobs.event_storm'
  if (msgId.endsWith('-vangogh-prototype')) return 'vangogh.prototype'
  if (/-diagram-\d+$/.test(msgId)) return 'vangogh.diagrams'
  return null
}

function isMermaidDsl(content: string): boolean {
  const t = content.trim().toLowerCase()
  return /^(graph |flowchart |classdiagram|sequencediagram|erdiagram|statediagram|gantt|pie |journey|gitgraph|mindmap|timeline)/.test(t)
}

interface StepState {
  step: string
  status: 'pending' | 'active' | 'done'
  messages: AgentEvent[]
}

function buildStepStates(events: AgentEvent[]): Record<string, StepState> {
  const states: Record<string, StepState> = {}
  for (const s of STEPS) {
    states[s.key] = { step: s.key, status: 'pending', messages: [] }
  }

  const bodies = aggregatedTextMessageBodies(events)
  for (const ev of events) {
    const kind = effectiveEventKind(ev)
    const stepName = effectivePipelineStep(ev)
    if (kind === 'STEP_STARTED' && stepName) {
      states[stepName] = { step: stepName, status: 'active', messages: states[stepName]?.messages || [] }
    }
    if (kind === 'STEP_FINISHED' && stepName) {
      states[stepName] = { step: stepName, status: 'done', messages: states[stepName]?.messages || [] }
    }
    if (kind === 'TEXT_MESSAGE_CONTENT') {
      const msgId = effectiveMessageId(ev)
      const text = bodies.get(msgId) || effectiveTextDelta(ev)
      const targetStep = stepFromMessageId(msgId)
      // Não descartar Mermaid — resumir para o painel de raciocínio
      let displayText = text
      if (isMermaidDsl(text)) {
        const indexMatch = msgId.match(/-diagram-(\d+)$/)
        const index = indexMatch ? indexMatch[1] : '?'
        const lineCount = text.split('\n').length
        displayText = `[Diagrama ${index} gerado — ${lineCount} linhas]`
      }
      const activeMsg: AgentEvent = { ...ev, content: displayText, delta: displayText }
      if (targetStep && states[targetStep]) {
        const arr = states[targetStep].messages
        const idx = arr.findIndex((m) => effectiveMessageId(m) === msgId)
        if (idx >= 0) arr.splice(idx, 1)
        arr.push(activeMsg)
      } else {
        const active = Object.values(states).find((s) => s.status === 'active')
        if (active) {
          const arr = active.messages
          const idx = arr.findIndex((m) => effectiveMessageId(m) === msgId)
          if (idx >= 0) arr.splice(idx, 1)
          arr.push(activeMsg)
        }
      }
    }
  }
  return states
}

/** HITL via CUSTOM `archforge.hitl.pause`, STATE_DELTA /hitl ou legado RUN_PAUSED */
function extractConflict(events: AgentEvent[]): { hasConflict: boolean; requiresHitl: boolean; conflictData: Record<string, unknown> | null } {
  const customPause = events.find(
    (e) => effectiveEventKind(e) === 'CUSTOM' && (e as AgentEvent & { name?: string }).name === 'archforge.hitl.pause',
  ) as (AgentEvent & { value?: Record<string, unknown> }) | undefined
  const legacy = events.find((e) => effectiveEventKind(e) === 'RUN_PAUSED')
  const stateDelta = events.find((e) => effectiveEventKind(e) === 'STATE_DELTA')

  let conflictObj: Record<string, unknown> | null = null

  if (customPause?.value?.conflict && typeof customPause.value.conflict === 'object') {
    conflictObj = customPause.value.conflict as Record<string, unknown>
  } else if (legacy?.conflict && typeof legacy.conflict === 'object') {
    conflictObj = legacy.conflict
  } else if (stateDelta && Array.isArray((stateDelta as Record<string, unknown>).delta)) {
    const delta = (stateDelta as Record<string, unknown>).delta as Array<{ path?: string; value?: unknown }>
    const hitl = delta.find((d) => d.path === '/hitl')?.value as Record<string, unknown> | undefined
    if (hitl?.conflict && typeof hitl.conflict === 'object') {
      conflictObj = hitl.conflict as Record<string, unknown>
    }
  }

  if (!conflictObj) return { hasConflict: false, requiresHitl: false, conflictData: null }

  return {
    hasConflict: Boolean(conflictObj.has_conflict),
    requiresHitl: Boolean(conflictObj.requires_hitl),
    conflictData: conflictObj,
  }
}

export default function AgentReasoningPanel({
  events,
  isOpen,
  onClose,
  onHitlApprove,
  onHitlReject,
  onHitlOverride,
}: AgentReasoningPanelProps) {
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set(['dochie.review']))
  const [expandedReviewers, setExpandedReviewers] = useState<Set<string>>(new Set())
  const [hitlFeedback, setHitlFeedback] = useState('')
  const [hitlActionTaken, setHitlActionTaken] = useState<string | null>(null)

  const messageBodies = useMemo(() => aggregatedTextMessageBodies(events), [events])

  const runId = useMemo(() => {
    const ev = [...events].reverse().find((e) => effectiveEventKind(e) === 'RUN_STARTED')
    const rid = effectiveRunId(ev ?? {})
    return rid ? rid.slice(-6) : '—'
  }, [events])

  const isFinished = useMemo(
    () => events.some((e) => effectiveEventKind(e) === 'RUN_FINISHED'),
    [events],
  )

  const isPaused = useMemo(
    () =>
      events.some((e) => {
        const k = effectiveEventKind(e)
        return k === 'RUN_PAUSED' || (k === 'CUSTOM' && (e as AgentEvent).name === 'archforge.hitl.pause')
      }),
    [events],
  )

  const pauseReason = useMemo(() => {
    const rev = [...events].reverse()
    const legacy = rev.find((e) => effectiveEventKind(e) === 'RUN_PAUSED')
    const custom = rev.find(
      (e) => effectiveEventKind(e) === 'CUSTOM' && (e as AgentEvent).name === 'archforge.hitl.pause',
    ) as (AgentEvent & { value?: { reason?: string } }) | undefined
    const fromCustom = custom?.value?.reason
    if (fromCustom) return fromCustom
    return typeof legacy?.reason === 'string' ? legacy.reason : ''
  }, [events])
  const { conflictData } = useMemo(() => extractConflict(events), [events])

  const stepStates = useMemo(() => buildStepStates(events), [events])

  const doneCount = STEPS.filter((s) => stepStates[s.key].status === 'done').length
  const progressPct = Math.round((doneCount / STEPS.length) * 100)

  const toggleStep = (step: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev)
      if (next.has(step)) next.delete(step)
      else next.add(step)
      return next
    })
  }

  const toggleReviewer = (key: string) => {
    setExpandedReviewers((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const adrRef: HitlAdrRef | undefined = conflictData
    ? {
        adrId: conflictData.adr_id ? String(conflictData.adr_id) : null,
        adrTitle: conflictData.adr_title ? String(conflictData.adr_title) : null,
      }
    : undefined

  const handleApprove = () => {
    setHitlActionTaken('approved')
    onHitlApprove?.(adrRef)
  }

  const handleReject = () => {
    setHitlActionTaken('rejected')
    onHitlReject?.(adrRef)
  }

  const handleOverride = () => {
    if (!hitlFeedback.trim()) return
    setHitlActionTaken('override')
    onHitlOverride?.(hitlFeedback.trim(), adrRef)
    setHitlFeedback('')
  }

  // Fecha com ESC (o painel é um diálogo modal)
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  // Determine HITL display mode
  // If RUN_FINISHED arrived, this is post-run review, not an active pause
  const hitlMode: 'active-pause' | 'post-run-review' | 'none' = isPaused
    ? isFinished
      ? 'post-run-review'
      : 'active-pause'
    : 'none'

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Raciocínio do time de agentes"
        className="w-full max-w-2xl max-h-[85vh] bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <Bot size={16} className="text-agent" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Raciocínio do Agente</h3>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-mono">#{runId}</span>
            {isFinished && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium">Concluído</span>
            )}
            {hitlMode === 'active-pause' && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 font-medium">Aguardando decisão</span>
            )}
            {hitlMode === 'post-run-review' && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 font-medium">Revisão pendente</span>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar painel de raciocínio"
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-4 pt-3 pb-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">Progresso</span>
            <span className="text-[10px] text-gray-500 dark:text-gray-400">{doneCount} de {STEPS.length} etapas</span>
          </div>
          <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-agent rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {/* Step dots */}
          <div className="flex justify-between mt-2">
            {STEPS.map((s) => {
              const st = stepStates[s.key]
              const Icon = s.icon
              const isDone = st.status === 'done'
              const isActive = st.status === 'active'
              return (
                <div key={s.key} className="flex flex-col items-center gap-1">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors ${
                      isDone
                        ? 'bg-green-500 text-white'
                        : isActive
                        ? 'bg-agent text-white ring-2 ring-agent/30 animate-pulse'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
                    }`}
                  >
                    {isDone ? <CheckCircle size={12} /> : <Icon size={12} />}
                  </div>
                  <span className={`text-[9px] font-medium ${isDone || isActive ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-600'}`}>
                    {s.short}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* HITL Alert with actions */}
        {hitlMode !== 'none' && (
          <div className="mx-4 mt-2 px-3 py-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="text-yellow-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-yellow-800 dark:text-yellow-300">
                  {hitlMode === 'active-pause' ? 'Decisão humana pendente' : 'Conflitos detectados — revise as decisões'}
                </p>
                <p className="text-[10px] text-yellow-700 dark:text-yellow-400 mt-0.5">
                  {pauseReason === 'adr_pending_hitl' || pauseReason === 'conflict_detected'
                    ? 'Archie identificou uma decisão arquitetural que requer julgamento humano. O pipeline seguiu, mas revise o ADR pendente.'
                    : pauseReason}
                </p>

                {/* ADR / Conflict details */}
                {conflictData && (
                  <div className="mt-2 space-y-1">
                    {!!conflictData.adr_id && (
                      <p className="text-[10px] text-yellow-700 dark:text-yellow-400">
                        <span className="font-medium">ADR:</span> {String(conflictData.adr_id)}
                        {conflictData.adr_title ? ` — ${String(conflictData.adr_title)}` : ''}
                      </p>
                    )}
                    {!!conflictData.conflict_type && (
                      <p className="text-[10px] text-yellow-700 dark:text-yellow-400">
                        <span className="font-medium">Tipo:</span> {String(conflictData.conflict_type)}
                      </p>
                    )}
                    {!!conflictData.resolution_proposal && (
                      <p className="text-[10px] text-yellow-700 dark:text-yellow-400">
                        <span className="font-medium">Proposta:</span> {String(conflictData.resolution_proposal)}
                      </p>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                {!hitlActionTaken ? (
                  <div className="mt-3 flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleApprove}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                      >
                        <ThumbsUp size={12} />
                        Aprovar recomendação
                      </button>
                      <button
                        onClick={handleReject}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                      >
                        <ThumbsDown size={12} />
                        Rejeitar
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={hitlFeedback}
                        onChange={(e) => setHitlFeedback(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleOverride()}
                        placeholder="Escreva uma observação para orientar a próxima geração..."
                        aria-label="Observação para orientar a próxima geração"
                        className="flex-1 min-w-0 px-2.5 py-1.5 rounded-md text-[11px] bg-white dark:bg-gray-800 border border-yellow-300 dark:border-yellow-700 text-gray-700 dark:text-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-yellow-400"
                      />
                      <button
                        onClick={handleOverride}
                        disabled={!hitlFeedback.trim()}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <MessageSquarePlus size={12} />
                        Enviar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 flex items-center gap-1.5 text-[11px] font-medium">
                    {hitlActionTaken === 'approved' && (
                      <>
                        <CheckCircle size={12} className="text-green-600" />
                        <span className="text-green-700 dark:text-green-300">
                          ADR aprovado. O Archie vai manter esta decisão na próxima geração dos artefatos.
                        </span>
                      </>
                    )}
                    {hitlActionTaken === 'rejected' && (
                      <>
                        <AlertCircle size={12} className="text-red-600" />
                        <span className="text-red-700 dark:text-red-300">
                          ADR rejeitado. Na próxima geração o Archie vai propor uma alternativa — clique em Regenerar quando quiser aplicar.
                        </span>
                      </>
                    )}
                    {hitlActionTaken === 'override' && (
                      <>
                        <Send size={12} className="text-yellow-600" />
                        <span className="text-yellow-700 dark:text-yellow-300">
                          Observação registrada. Ela será incorporada pelo Archie na próxima geração dos artefatos.
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto custom-scrollbar px-4 py-4 space-y-3">
          {events.length === 0 && (
            <div className="text-center text-xs text-gray-400 dark:text-gray-600 py-8">Nenhum evento registrado ainda.</div>
          )}

          {STEPS.map((stepDef) => {
            const state = stepStates[stepDef.key]
            if (state.status === 'pending' && state.messages.length === 0) return null // Hide pending empty steps

            const meta = AGENT_META[stepDef.agent]
            const isExpanded = expandedSteps.has(stepDef.key)
            const hasMessages = state.messages.length > 0
            const Icon = stepDef.icon

            return (
              <div
                key={stepDef.key}
                className={`rounded-lg border overflow-hidden transition-all ${
                  state.status === 'done'
                    ? `${meta.border} ${meta.bg}`
                    : state.status === 'active'
                    ? 'border-agent/30 bg-agent/5 dark:bg-agent/10'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/20'
                }`}
              >
                {/* Step header */}
                <button
                  onClick={() => hasMessages && toggleStep(stepDef.key)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left ${hasMessages ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                      state.status === 'done'
                        ? 'bg-green-500 text-white'
                        : state.status === 'active'
                        ? 'bg-agent text-white animate-pulse'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                    }`}
                  >
                    {state.status === 'done' ? <CheckCircle size={12} /> : <Icon size={12} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">{stepDef.label}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${meta.badge}`}>{meta.name}</span>
                      {state.status === 'active' && <Loader2 size={12} className="text-agent animate-spin" />}
                    </div>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">
                      {state.status === 'pending' ? 'Aguardando...' : state.status === 'active' ? 'Processando...' : `${state.messages.length} mensagem(ns)`}
                    </span>
                  </div>
                  {hasMessages && (
                    <span className="text-gray-400 dark:text-gray-600">{isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
                  )}
                </button>

                {/* Step content */}
                {isExpanded && hasMessages && (
                  <div className="px-3 pb-3 space-y-2 border-t border-gray-100 dark:border-gray-800">
                    {stepDef.key === 'dochie.review' ? (
                      // L2: Reviewer grid
                      <ReviewerGrid
                        messages={state.messages}
                        expanded={expandedReviewers}
                        toggle={toggleReviewer}
                        bodies={messageBodies}
                      />
                    ) : (
                      state.messages.map((msg, mIdx) =>
                        renderMessage(msg, `${stepDef.key}-${mIdx}`, messageBodies),
                      )
                    )}
                  </div>
                )}

                {/* ADR pendente HITL: detalhes embutidos no step de síntese (Archie) */}
                {stepDef.key === 'archie.synthesis' && (isExpanded || hitlMode !== 'none') && conflictData && (
                  <div className="px-3 pb-3 space-y-2 border-t border-gray-100 dark:border-gray-800">
                    <ConflictDetail conflict={conflictData} />
                  </div>
                )}
              </div>
            )
          })}

          {/* Run end marker */}
          {isFinished && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300">
              <CheckCircle size={14} />
              Análise concluída — todos os artefatos foram gerados
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Render ADR HITL pendente (novo formato) ou ConflictReport (formato legado),
 * dependendo de qual veio no payload.
 */
function ConflictDetail({ conflict }: { conflict: Record<string, unknown> }) {
  const positions = conflict.positions as Array<Record<string, unknown>> | undefined
  const isAdrPending = !!conflict.adr_id || conflict.source === 'adr'
  const title = isAdrPending ? 'ADR pendente HITL' : 'Detalhes do conflito'

  return (
    <div className="rounded-md bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 p-2.5 space-y-2">
      <div className="flex items-center gap-1.5">
        <Eye size={12} className="text-amber-600" />
        <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide">
          {title}
        </span>
      </div>
      {isAdrPending && !!conflict.adr_id && (
        <p className="text-[11px] text-gray-700 dark:text-gray-300">
          <span className="font-medium">{String(conflict.adr_id)}</span>
          {conflict.adr_title ? `: ${String(conflict.adr_title)}` : ''}
        </p>
      )}
      {isAdrPending && !!conflict.block && (
        <pre className="text-[10px] font-mono whitespace-pre-wrap bg-white/60 dark:bg-black/30 rounded p-2 text-gray-700 dark:text-gray-300 max-h-48 overflow-auto">
          {String(conflict.block)}
        </pre>
      )}
      {!isAdrPending && !!conflict.conflict_type && (
        <p className="text-[11px] text-gray-700 dark:text-gray-300">
          <span className="font-medium">Tipo:</span> {String(conflict.conflict_type)}
        </p>
      )}
      {!isAdrPending && !!conflict.resolution_proposal && (
        <p className="text-[11px] text-gray-700 dark:text-gray-300">
          <span className="font-medium">Proposta de resolução:</span> {String(conflict.resolution_proposal)}
        </p>
      )}
      {!isAdrPending && positions && positions.length > 0 && (
        <div className="space-y-1">
          <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Posições em conflito:</span>
          {positions.map((pos, i) => (
            <div key={i} className="text-[11px] text-gray-600 dark:text-gray-400 pl-2 border-l-2 border-amber-300 dark:border-amber-700">
              {!!pos.agent ? <span className="font-medium">{String(pos.agent)}:</span> : null}{' '}
              {!!pos.position ? String(pos.position) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** L2: 4 reviewers as compact chips with expandable detail */
function ReviewerGrid({
  messages,
  expanded,
  toggle,
  bodies,
}: {
  messages: AgentEvent[]
  expanded: Set<string>
  toggle: (k: string) => void
  bodies: Map<string, string>
}) {
  // Group messages by reviewer
  const byReviewer: Record<string, { meta: typeof REVIEWER_META[keyof typeof REVIEWER_META]; messages: AgentEvent[] }> = {}

  for (const msg of messages) {
    const msgId = effectiveMessageId(msg)
    const match = msgId.match(/(tech_reviewer|risk_reviewer|innovation_reviewer|sustainability_reviewer)/)
    const key = match?.[1] || 'unknown'
    const meta = REVIEWER_META[key]
    if (!meta) continue
    if (!byReviewer[key]) byReviewer[key] = { meta, messages: [] }
    byReviewer[key].messages.push(msg)
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(byReviewer).map(([key, { meta, messages }]) => {
          const Icon = meta.icon
          // Parse first message for feasibility badge
          const firstId = effectiveMessageId(messages[0] ?? {})
          const fullText = bodies.get(firstId) || String(messages[0]?.delta ?? messages[0]?.content ?? '')
          const parsed = tryParseReviewer(fullText)
          const isExpanded = expanded.has(key)
          const feasibility = parsed?.feasibility || 'desconhecido'
          const badgeClass =
            feasibility === 'viavel'
              ? 'bg-green-100 text-green-700'
              : feasibility === 'inviavel'
              ? 'bg-red-100 text-red-700'
              : 'bg-yellow-100 text-yellow-700'

          return (
            <div key={key} className={`rounded-md border p-2 ${meta.bg} border-gray-100 dark:border-gray-700`}>
              <button onClick={() => toggle(key)} className="w-full flex items-center gap-1.5 text-left">
                <Icon size={14} className={meta.color} />
                <span className={`text-[11px] font-semibold ${meta.color}`}>{meta.name}</span>
                <span className={`text-[9px] px-1 py-0.5 rounded-full font-medium ml-auto ${badgeClass}`}>
                  {feasibilityLabel(feasibility)}
                </span>
              </button>
              {parsed?.perspective && (
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 italic line-clamp-1">{parsed.perspective}</p>
              )}
              {isExpanded && parsed && (
                <div className="mt-2 space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                  {parsed.risks.length > 0 && (
                    <div>
                      <span className="text-[9px] font-medium text-red-500">Riscos</span>
                      <ul className="mt-0.5 space-y-0.5">
                        {parsed.risks.map((r, i) => (
                          <li key={i} className="text-[10px] text-gray-600 dark:text-gray-400 flex items-start gap-1">
                            <AlertCircle size={8} className="text-red-400 mt-0.5 shrink-0" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {parsed.recommendations.length > 0 && (
                    <div>
                      <span className="text-[9px] font-medium text-green-500">Recomendações</span>
                      <ul className="mt-0.5 space-y-0.5">
                        {parsed.recommendations.map((r, i) => (
                          <li key={i} className="text-[10px] text-gray-600 dark:text-gray-400 flex items-start gap-1">
                            <CheckCircle size={8} className="text-green-400 mt-0.5 shrink-0" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Render a single message based on its type */
function renderMessage(msg: AgentEvent, key: string, bodies: Map<string, string>) {
  const mid = effectiveMessageId(msg)
  const text = bodies.get(mid) || String(msg.delta ?? msg.content ?? '')

  if (mid.endsWith('-architect')) {
    return (
      <div key={key} className="py-1">
        <div className="flex items-center gap-1.5 mb-1">
          <FileText size={12} className="text-violet-500" />
          <span className="text-[10px] font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wide">
            Software Design Document
          </span>
        </div>
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded p-2">
          <p className="text-[11px] text-gray-600 dark:text-gray-400 whitespace-pre-wrap line-clamp-4">
            {text.slice(0, 500)}
            {text.length > 500 && '...'}
          </p>
        </div>
      </div>
    )
  }

  if (mid.endsWith('-ingest')) {
    return (
      <div key={key} className="flex items-start gap-2 py-1">
        <Terminal size={12} className="text-gray-400 mt-0.5 shrink-0" />
        <p className="text-[11px] text-gray-600 dark:text-gray-400">{text}</p>
      </div>
    )
  }

  if (/-diagram-\d+$/.test(mid)) {
    return (
      <div key={key} className="flex items-start gap-2 py-1">
        <PenTool size={12} className="text-rose-500 mt-0.5 shrink-0" />
        <p className="text-[11px] text-rose-600 dark:text-rose-400 font-medium">{text}</p>
      </div>
    )
  }

  if (mid.endsWith('-jobs-event-storm')) {
    const isError = text.startsWith('[ERRO]')
    let summary = text
    if (!isError) {
      try {
        const parsed = JSON.parse(text) as { elements?: unknown[]; narrative?: string }
        const count = Array.isArray(parsed.elements) ? parsed.elements.length : 0
        summary = `[Event Storm gerado — ${count} elementos]${parsed.narrative ? `\n${parsed.narrative}` : ''}`
      } catch {
        summary = '[Event Storm gerado]'
      }
    }
    return (
      <div key={key} className="flex items-start gap-2 py-1">
        <Compass size={12} className="text-amber-500 mt-0.5 shrink-0" />
        <p className="text-[11px] text-amber-600 dark:text-amber-400 font-medium whitespace-pre-wrap">
          {summary}
        </p>
      </div>
    )
  }

  if (mid.endsWith('-vangogh-prototype')) {
    const isError = text.startsWith('[ERRO]')
    let summary = text
    if (!isError) {
      try {
        const parsed = JSON.parse(text) as { pages?: unknown[]; title?: string }
        const count = Array.isArray(parsed.pages) ? parsed.pages.length : 0
        summary = `[Protótipo gerado — ${count} páginas${parsed.title ? `: ${parsed.title}` : ''}]`
      } catch {
        summary = '[Protótipo gerado]'
      }
    }
    return (
      <div key={key} className="flex items-start gap-2 py-1">
        <PenTool size={12} className="text-rose-500 mt-0.5 shrink-0" />
        <p className="text-[11px] text-rose-600 dark:text-rose-400 font-medium">{summary}</p>
      </div>
    )
  }

  return (
    <div key={key} className="flex items-start gap-2 py-1">
      <Terminal size={12} className="text-gray-400 mt-0.5 shrink-0" />
      <p className="text-[11px] text-gray-600 dark:text-gray-400 whitespace-pre-wrap line-clamp-3">{text}</p>
    </div>
  )
}

interface ParsedReviewer {
  agent_name: string
  perspective: string
  feasibility: string
  confidence: number
  risks: string[]
  recommendations: string[]
}

function tryParseReviewer(content: string): ParsedReviewer | null {
  const agentNameMatch = content.match(/agent_name\s*=\s*['"]([^'"]+)['"]/i) || content.match(/\[([^\]]*reviewer[^\]]*)\]/i)
  const perspectiveMatch = content.match(/perspective\s*=\s*['"]([^'"]+)['"]/i)
  const feasibilityMatch = content.match(/feasibility\s*=\s*['"]([^'"]+)['"]/i)
  const confidenceMatch = content.match(/confidence\s*=\s*(\d+)/i)

  const risks: string[] = []
  const risksMatch = content.match(/risks\s*=\s*\[([^\]]*)\]/s)
  if (risksMatch) {
    for (const m of risksMatch[1].matchAll(/['"]([^'"]*)['"]/g)) {
      if (m[1].trim()) risks.push(m[1].trim())
    }
  }

  const recommendations: string[] = []
  const recsMatch = content.match(/recommendations\s*=\s*\[([^\]]*)\]/s)
  if (recsMatch) {
    for (const m of recsMatch[1].matchAll(/['"]([^'"]*)['"]/g)) {
      if (m[1].trim()) recommendations.push(m[1].trim())
    }
  }

  if (!agentNameMatch && !perspectiveMatch && risks.length === 0) return null

  return {
    agent_name: agentNameMatch?.[1]?.replace(/[\[\]]/g, '').trim() || 'Reviewer',
    perspective: perspectiveMatch?.[1] || '',
    feasibility: feasibilityMatch?.[1] || 'desconhecido',
    confidence: confidenceMatch ? parseInt(confidenceMatch[1]) : 0,
    risks,
    recommendations,
  }
}
